import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, MessageSquareText } from 'lucide-react';

const Bouton = ({ onClick, children, testId, plein, disabled }) => (
  <button type="button" onClick={onClick} disabled={disabled} data-testid={testId}
    className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors disabled:opacity-50 ${plein ? 'bg-[#0E7C74] text-white hover:bg-[#0B6660]' : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}>
    {children}
  </button>
);

// Commentaire du formateur sur un indicateur : écrit par l'équipe (vue lecture seule), lu par le stagiaire
export const CommentaireGuide = ({ i, progression }) => {
  const com = progression.commentaires[i.id];
  const [texte, setTexte] = useState(com?.texte || '');
  const [edition, setEdition] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  useEffect(() => { if (!edition) setTexte(com?.texte || ''); }, [com, edition]);
  if (!com && !progression.peutCommenter) return null;

  const enregistrer = async (t) => {
    setEnvoi(true);
    try {
      const { data } = await axios.put(`/guide-bpjeps/suivi/${progression.uid}/commentaire`, { indicateur: i.id, texte: t });
      progression.majCommentaire(i.id, data.commentaire);
      setTexte(data.commentaire?.texte || '');
      setEdition(false);
      toast.success(data.commentaire ? 'Commentaire enregistré : le stagiaire est prévenu' : 'Commentaire supprimé');
    } catch {
      toast.error('Enregistrement impossible, réessayez.');
    } finally {
      setEnvoi(false);
    }
  };

  const ecrire = progression.peutCommenter && (edition || !com);
  return (
    <div className="border-t border-teal-100 bg-teal-50/60 p-4 sm:p-5" data-testid={`guide-commentaire-${i.id}`}>
      <p className="flex flex-wrap items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-[#0E7C74]">
        <MessageSquareText className="h-3.5 w-3.5" /> Commentaire du formateur
        {com && (
          <span className="w-full font-medium normal-case tracking-normal text-slate-500 sm:w-auto" data-testid={`guide-commentaire-auteur-${i.id}`}>
            <span className="hidden sm:inline">· </span>{com.auteur_nom}, le {new Date(com.date).toLocaleDateString('fr-FR')}
          </span>
        )}
      </p>
      {ecrire ? (
        <>
          <textarea value={texte} onChange={(e) => setTexte(e.target.value)} rows={2} data-testid={`guide-commentaire-saisie-${i.id}`}
            placeholder="Votre conseil au stagiaire sur cet indicateur…"
            className="mt-2 w-full resize-y rounded-xl border border-teal-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#0E7C74]/40" />
          <div className="mt-2 flex gap-2">
            <Bouton plein onClick={() => enregistrer(texte)} disabled={envoi || !texte.trim()} testId={`guide-commentaire-enregistrer-${i.id}`}>
              {envoi && <Loader2 className="h-3.5 w-3.5 animate-spin" />} Enregistrer
            </Bouton>
            {com && <Bouton onClick={() => { setTexte(com.texte); setEdition(false); }} testId={`guide-commentaire-annuler-${i.id}`}>Annuler</Bouton>}
          </div>
        </>
      ) : (
        <>
          <p className="mt-1.5 whitespace-pre-line text-sm text-slate-700" data-testid={`guide-commentaire-texte-${i.id}`}>{com.texte}</p>
          {progression.peutCommenter && (
            <div className="mt-2 flex gap-2">
              <Bouton onClick={() => setEdition(true)} testId={`guide-commentaire-modifier-${i.id}`}>Modifier</Bouton>
              <Bouton onClick={() => enregistrer('')} disabled={envoi} testId={`guide-commentaire-supprimer-${i.id}`}>Supprimer</Bouton>
            </div>
          )}
        </>
      )}
    </div>
  );
};
